import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import {
  KTextInput,
  KButton,
  KCheckbox,
  KIcon,
  KSelectAutocomplete,
} from "@kyburz/kdesign-system";
import update from "immutability-helper";
import useSetState from "../hooks/useSetState";
import { deepEqual } from "../utils/utils";

import Editable from "./ui/Editable";

import {
  getVehicleTemplate,
  getVehicleTemplateParameters,
} from "../apiCalls/admin/vehicleTemplates";
import { setVehicleTemplate } from "../redux/actions/actions";
import { RootStateInterface } from "../interfaces/ReduxInterface";
import KInfiniteTable from "./table/KInfiniteTable";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      updateMyData={props.updateMyData}
    />
  ),
  (oldProps, newProps) => {
    if (oldProps.data.length !== newProps.data.length) return false;

    for (let i = 0; i < oldProps.data.length; i += 1) {
      const oldLine = oldProps.data[i];
      const newLine = newProps.data[i];

      if (oldLine.technicalName !== newLine.technicalName) return false;
      if (oldLine.isDefault !== newLine.isDefault) return false;
      if (oldLine.isDigital !== newLine.isDigital) return false;
      if (!deepEqual(oldLine.name, newLine.name)) return false;
      if (oldLine.hasHover !== newLine.hasHover) return false;
      if (!deepEqual(oldLine.hover, newLine.hover)) return false;
      if (oldLine.default !== newLine.default) return false;
    }
    return true;
  },
);

const useStyles = makeStyles(() => ({
  tableContainer: {
    height: "calc(100% - 100px)",
  },
}));

const VehicleTemplateList = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const rows =
    useSelector(
      (state: RootStateInterface) => state.vehicleTemplate.template,
    ) || [];

  const [{ query, selectedParameter, availableParameters }, setState] =
    useSetState({
      query: "",
      selectedParameter: "",
      availableParameters: [],
    });

  // Rows cruds
  const handleCopyDefault = async () => {
    const res = await getVehicleTemplate(currentCustomer?.shortname, "default");
    if (res.data) {
      const t: [] = res.data.default_columns;
      const digital = res.data.is_digital;
      dispatch(
        setVehicleTemplate(
          Object.entries(res.data.all_columns).map((el: any) => ({
            technicalName: el[0],
            // @ts-ignore
            isDefault: t.includes(el[0]),
            isDigital: digital.includes(el[0]),
            name: el[1].name,
            hasHover: el[1].has_hover,
            hover: el[1].hover,
            default: el[1].default,
          })),
        ),
      );
    }
  };

  const handleAddColumn = (parameter) => {
    if (parameter !== "") {
      setState({ selectedParameter: "" });
      dispatch(
        setVehicleTemplate([
          ...rows,
          {
            technicalName: parameter,
            isDefault: false,
            name: { en: "", de: "", fr: "", it: "" },
            hasHover: false,
            hover: { en: "", de: "", fr: "", it: "" },
            default: "-",
          },
        ]),
      );
    }
  };

  const handleSelect = (name, el) => setState({ selectedParameter: el.label });

  useEffect(() => {
    getVehicleTemplateParameters().then((res) =>
      setState({ availableParameters: res.data }),
    );
  }, []);

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const columns = React.useMemo(
    () => [
      {
        Header: "Technical Name",
        accessor: "technicalName",
        width: 240,
      },
      {
        id: "isDefault",
        Header: "Is Default?",
        Cell: (row) => (
          <KCheckbox
            name="isDefault"
            checked={row.row.original.isDefault}
            style={{ marginTop: 0 }}
            handleChange={(e) => {
              const newRow = update(row.row.original, {
                isDefault: { $set: e.target.checked },
              });
              const newRows = [...row.data];
              newRows[row.row.index] = newRow;
              dispatch(setVehicleTemplate(newRows));
            }}
          />
        ),
        width: 90,
      },
      {
        id: "isDigital",
        Header: "Is Digital?",
        Cell: (row) => (
          <KCheckbox
            name="isDigital"
            checked={row.row.original.isDigital}
            style={{ marginTop: 0 }}
            handleChange={(e) => {
              const newRow = update(row.row.original, {
                isDigital: { $set: e.target.checked },
              });
              const newRows = [...row.data];
              newRows[row.row.index] = newRow;
              dispatch(setVehicleTemplate(newRows));
            }}
          />
        ),
        width: 90,
      },
      {
        Header: "Default Value",
        accessor: "default",
        Cell: (row) => (
          <Editable
            text={row.row.original.default}
            onChange={(val) => {
              const newRow = update(row.row.original, {
                default: { $set: val },
              });
              const newRows = [...row.data];
              newRows[row.row.index] = newRow;
              dispatch(setVehicleTemplate(newRows));
            }}
          />
        ),
        width: 110,
      },
      {
        Header: "Column Name",
        columns: [
          {
            id: "NameEnglish",
            Header: "English",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.en}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    name: { en: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
          {
            id: "NameGerman",
            Header: "German",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.de}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    name: { de: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
          {
            id: "NameFrench",
            Header: "French",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.fr}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    name: { fr: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
          {
            id: "NameItalian",
            Header: "Italian",
            Cell: (row) => (
              <Editable
                text={row.row.original.name.it}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    name: { it: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
        ],
      },
      {
        id: "hasHover",
        Header: "Hover?",
        Cell: (row) => (
          <KCheckbox
            name="hasHover"
            checked={row.row.original.hasHover}
            style={{ marginTop: 0 }}
            handleChange={(e) => {
              const newRow = update(row.row.original, {
                hasHover: { $set: e.target.checked },
              });
              const newRows = [...row.data];
              newRows[row.row.index] = newRow;
              dispatch(setVehicleTemplate(newRows));
            }}
          />
        ),
        width: 80,
      },
      {
        Header: "Hover Text",
        columns: [
          {
            id: "HoverEnglish",
            Header: "English",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.en}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    hover: { en: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
          {
            id: "HoverGerman",
            Header: "German",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.de}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    hover: { de: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
          {
            id: "HoverFrench",
            Header: "French",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.fr}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    hover: { fr: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
          {
            id: "HoverItalian",
            Header: "Italian",
            Cell: (row) => (
              <Editable
                text={row.row.original.hover.it}
                onChange={(val) => {
                  const newRow = update(row.row.original, {
                    hover: { it: { $set: val } },
                  });
                  const newRows = [...row.data];
                  newRows[row.row.index] = newRow;
                  dispatch(setVehicleTemplate(newRows));
                }}
              />
            ),
          },
        ],
      },
      {
        id: "actions",
        Header: "Actions",
        Cell: (row) => (
          <div
            style={{ display: "inline-block", cursor: "pointer" }}
            onClick={() => {
              const newRows = row.data.filter(
                (el, index) => index !== row.row.index,
              );
              dispatch(setVehicleTemplate(newRows));
            }}
          >
            <KIcon icon="trash-2" color="#383F51" size={20} />
          </div>
        ),
        width: 60,
      },
    ],
    [],
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "flex" }}>
          <KSelectAutocomplete
            elements={availableParameters.map((el) => ({
              label: el.parameter,
              value: el._id,
            }))}
            value={selectedParameter}
            onChange={handleSelect}
            size="small"
            style={{ marginRight: "16px" }}
            name="selectedParameter"
          />

          <KButton
            children="Add"
            theme="primary"
            onClick={() => handleAddColumn(selectedParameter)}
            size="small"
          />
        </div>
        <div style={{ display: "flex" }}>
          <KTextInput
            onChange={handleChange}
            name="query"
            placeholder="Enter your parameter"
            size="small"
            value={query}
            style={{ marginRight: "16px" }}
            disabled
          />
          <KButton
            children="Copy Default"
            theme="primary"
            onClick={handleCopyDefault}
            size="small"
          />
        </div>
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          data={React.useMemo(() => rows, [rows])}
          columns={columns}
          hasNextPage={false}
          isNextPageLoading={false}
        />
      </div>
    </>
  );
};

export default VehicleTemplateList;
